import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Card, CardContent, Typography, Button, Grid, Alert, CircularProgress, Divider } from '@mui/material';
import { CreditCard } from '@mui/icons-material';
import { AppDispatch } from '../redux/store/store';
import { getCreditPackages } from '../redux/billing/billingSlice';
import { selectCreditPackages, selectBillingLoading, selectBillingError } from '../redux/billing/selectors';
import PaymentForm from './PaymentForm';

const BillingSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const packages = useSelector(selectCreditPackages);
  const isLoading = useSelector(selectBillingLoading);
  const error = useSelector(selectBillingError);

  const [selectedPackageId, setSelectedPackageId] = React.useState<string | null>(null);

  React.useEffect(() => {
    // Only fetch if we don't have packages yet
    if (packages.length === 0 && !isLoading) {
      dispatch(getCreditPackages());
    }
  }, [dispatch, packages.length, isLoading]);

  const selectedPackage = React.useMemo(() => {
    if (!selectedPackageId) return null;
    return packages.find((p) => p.id === selectedPackageId) || null;
  }, [packages, selectedPackageId]);

  if (isLoading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant='h4' gutterBottom>
        Billing
      </Typography>

      {error && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {selectedPackage ? (
        <PaymentForm
          creditPackage={selectedPackage}
          onSuccess={() => {
            setSelectedPackageId(null);
          }}
          onCancel={() => setSelectedPackageId(null)}
        />
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Buy Credits
                </Typography>

                {packages.length === 0 ? (
                  <Alert severity='info'>No credit packages are available.</Alert>
                ) : (
                  <Box>
                    {packages.map((pkg) => (
                      <Box key={pkg.id} sx={{ mb: 2 }}>
                        <Grid container spacing={2} alignItems='center'>
                          <Grid item xs={8}>
                            <Typography variant='subtitle1'>{pkg.name}</Typography>
                            <Typography variant='body2' color='text.secondary'>
                              {pkg.description}
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                              ${pkg.price} for ${pkg.credit} credits
                            </Typography>
                          </Grid>
                          <Grid item xs={4} textAlign='right'>
                            <Button variant='contained' startIcon={<CreditCard />} onClick={() => setSelectedPackageId(pkg.id)} disabled={isLoading}>
                              Buy
                            </Button>
                          </Grid>
                        </Grid>
                        <Divider sx={{ mt: 2 }} />
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default BillingSettings;
